import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import LanguageToggle from "@/components/LanguageToggle";
import Logo, { LogoMark } from "@/components/Logo";
import { Boxes, CalendarClock, ClipboardList, ArrowUpRight } from "lucide-react";

export default function Landing() {
  const { t, lang } = useLang();
  const { user } = useAuth();

  const features = [
    { icon: Boxes, key: "projects", n: "01" },
    { icon: CalendarClock, key: "timeline", n: "02" },
    { icon: ClipboardList, key: "snags", n: "03" },
  ];
  const desc = {
    projects: {
      en: "All active sites on one calm board with progress, dates and client context.",
      es: "Todas tus obras en un panel sereno con progreso, fechas y contexto del cliente.",
    },
    timeline: {
      en: "Every delivery and subcontractor arrival on a horizontal gantt — six weeks ahead at a glance.",
      es: "Cada entrega y llegada de subcontratista en un gantt horizontal — seis semanas a la vista.",
    },
    snags: {
      en: "Log site issues with photos, priorities and clear assignments — nothing slips through.",
      es: "Registra incidencias con fotos, prioridades y asignaciones claras — nada se pasa por alto.",
    },
  };

  return (
    <div className="min-h-screen bg-[#FAF9F4] text-neutral-950">
      <header className="glass-header sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size={26} />
          <div className="flex items-center gap-3">
            <LanguageToggle />
            {user ? (
              <Link to="/app/projects">
                <Button className="bg-black hover:bg-neutral-800 text-white rounded-full px-5" data-testid="nav-app-button">
                  {t("dashboard")}
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-link">
                  <Button variant="ghost" className="text-neutral-700 rounded-full">{t("sign_in")}</Button>
                </Link>
                <Link to="/register" data-testid="nav-register-link">
                  <Button className="bg-black hover:bg-neutral-800 text-white rounded-full px-5">{t("get_started")}</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="relative max-w-6xl mx-auto px-6 pt-24 pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-end">
            <div className="lg:col-span-7">
              <p className="label-eyebrow flex items-center">
                <span className="gold-rule" />{lang === "es" ? "Sistema operativo para la obra" : "Operating system for the site"}
              </p>
              <h1 className="mt-6 font-[Fraunces] text-[68px] md:text-[92px] leading-[0.95] tracking-tight text-neutral-950"
                style={{ fontVariationSettings: '"opsz" 144, "SOFT" 60' }}>
                Build with
                <br />
                <span className="display-italic text-[#B8933A]">clarity.</span>
              </h1>
              <p className="mt-8 text-neutral-600 text-lg max-w-xl leading-relaxed">
                {t("hero_sub")}
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link to={user ? "/app/projects" : "/register"} data-testid="cta-primary">
                  <Button size="lg" className="bg-black hover:bg-neutral-800 text-white rounded-full px-7 py-6 text-sm">
                    {t("get_started")} <ArrowUpRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </Link>
                <Link to="/login" data-testid="cta-signin">
                  <Button size="lg" variant="outline" className="rounded-full border-neutral-300 px-7 py-6 text-sm">
                    {t("sign_in")}
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="relative surface-card overflow-hidden shadow-sm">
                <img
                  src="https://images.pexels.com/photos/14989321/pexels-photo-14989321.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Construction"
                  className="w-full h-[440px] object-cover"
                />
                <div className="absolute top-4 left-4 right-4 flex items-start justify-between text-white">
                  <span className="label-eyebrow text-white/80">{lang === "es" ? "Obras activas" : "Active sites"}</span>
                  <span className="text-[10px] font-mono bg-black/40 backdrop-blur-sm px-2 py-1 rounded">{lang === "es" ? "En vivo" : "Live"}</span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent p-6">
                  <p className="font-[Fraunces] text-white text-4xl leading-none">12</p>
                  <p className="text-white/80 text-xs mt-1">3 {lang === "es" ? "vencen este mes" : "due this month"}</p>
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-black text-white p-5 rounded shadow-lg max-w-[220px] hidden md:block">
                <p className="label-eyebrow text-[#D4B570]">{lang === "es" ? "Incidencias críticas" : "Critical snags"}</p>
                <p className="font-[Fraunces] text-3xl mt-1">2</p>
                <p className="text-white/60 text-[11px] mt-1">{lang === "es" ? "Notificadas a jefes" : "Managers notified"}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Manifesto strip */}
      <section className="border-y border-neutral-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center md:text-left">
          {[
            { k: "01", l: lang === "es" ? "Proyectos activos" : "Active projects" },
            { k: "02", l: lang === "es" ? "Cronograma en vivo" : "Live timeline" },
            { k: "03", l: lang === "es" ? "Snags con foto" : "Photo snags" },
            { k: "04", l: lang === "es" ? "Informe semanal con IA" : "AI Powered Weekly Update" },
          ].map((x) => (
            <div key={x.k} className="flex items-baseline gap-3">
              <span className="font-[Fraunces] text-[#B8933A] text-2xl">{x.k}</span>
              <span className="text-neutral-600 text-sm">{x.l}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-16">
          <p className="label-eyebrow flex items-center">
            <span className="gold-rule" />{lang === "es" ? "Tres módulos" : "Three modules"}
          </p>
          <h2 className="mt-4 font-[Fraunces] text-4xl md:text-5xl leading-tight text-neutral-950">
            {lang === "es" ? (
              <>Todo lo que el jefe de obra <span className="display-italic text-[#B8933A]">realmente</span> abre.</>
            ) : (
              <>Everything the foreman <span className="display-italic text-[#B8933A]">actually</span> opens.</>
            )}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-[1px] bg-neutral-200 border border-neutral-200 rounded overflow-hidden">
          {features.map(({ icon: Icon, key, n }) => (
            <div key={key} className="bg-white p-8 group transition-colors duration-300 hover:bg-neutral-50">
              <div className="flex items-start justify-between">
                <Icon className="w-6 h-6 text-neutral-950" strokeWidth={1.5} />
                <span className="font-[Fraunces] text-[#B8933A] text-sm">{n} / 03</span>
              </div>
              <h3 className="mt-8 font-[Fraunces] font-medium text-2xl text-neutral-950">{t(key)}</h3>
              <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
                {desc[key][lang] || desc[key].en}
              </p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-neutral-200">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4 text-xs text-neutral-500">
          <div className="flex items-center gap-3">
            <LogoMark size={20} />
            <span>© 2026 obra.</span>
          </div>
          <span className="label-eyebrow">{lang === "es" ? "Construye con claridad" : "Build with clarity"}</span>
        </div>
      </footer>
    </div>
  );
}
